import "antd/dist/reset.css";
import { useEffect, useState } from "react";
import {
  Carousel,
  Button,
  Row,
  Col,
  Empty,
  Tag,
  Popconfirm,
  notification,
  Card as AntCard,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CheckOutlined,
  IdcardOutlined,
  WifiOutlined,
  BoldOutlined,
} from "@ant-design/icons";
import AddCardModal from "./AddCardModal";
import { controller } from "../../../payment/stripe/controller";

/**

 * props:
 * - title?: string
 * - loading?: boolean
 * - cards: Array<{
 * id: string,
 * brand?: string,       // 'visa' | 'mastercard' | ...
 * last4?: string,
 * exp_month?: number,
 * exp_year?: number,
 * name?: string,        // billing_details.name
 * isDefault?: boolean
 * }>
 * - onAddClick?: () => void
 * - onRemove?: (id: string) => void
 * - onMakeDefault?: (id: string) => void
 */
export default function MemberCardsCarousel({
  title = "Payment Cards",
  loading = false,
  cards = [],
  onRemove,
  onMakeDefault,
  memberId,
}) {
  const hasCards = Array.isArray(cards) && cards.length > 0;
  const [openModal, setOpenModal] = useState(false);
  const [cardList, setCardList] = useState([]);

  console.log(memberId);

  const getCards = async () => {
    if (!memberId) return;
    try {
      const response = await controller.getCardList(memberId);
      if (response.status < 250) {
        setCardList(response.json);
      } else {
        // Handle error case
        notification.error({
          message: "Error",
          description: "Could not initialize the card form. Please try again.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "A network error occurred. Please try again.",
      });
    } finally {
    }
  };

  const deleteCard = async (cardInfo) => {
    const pmId = cardInfo?.id;
    if (!memberId) return;

    try {
      const response = await controller.deleteCard(memberId, pmId);

      if (response.status < 250) {
        notification.success({
          message: "Card Deleted",
          description: "The card was successfully removed from this account.",
          placement: "bottom",
        });
        getCards();
      } else {
        notification.error({
          message: "Failed to Delete Card",
          description: response.data?.detail || "An unexpected error occurred.",
          placement: "bottom",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error Deleting Card",
        description:
          error.response?.data?.detail ||
          error.message ||
          "Something went wrong while deleting the card.",
        placement: "bottom",
      });
      console.error(error);
    }
  };

  const makeDefaultCard = async (cardInfo) => {
    const pmId = cardInfo?.id;
    if (!memberId) return;

    try {
      const response = await controller.makeDefaultCard(memberId, pmId);

      if (response.status < 250) {
        notification.success({
          message: "Default Card Updated",
          description:
            "This card has been successfully set as your default payment method.",
          placement: "bottom",
        });
        getCards();
      } else {
        notification.error({
          message: "Failed to Set Default Card",
          description:
            response.data?.detail ||
            "An unexpected error occurred while updating the default card.",
          placement: "bottom",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error Setting Default Card",
        description:
          error.response?.data?.detail ||
          error.message ||
          "Something went wrong while setting the default card.",
        placement: "bottom",
      });
      console.error(error);
    }
  };

  useEffect(() => {
    getCards();
  }, []);
  useEffect(() => {
    console.log("card", cardList);
  }, [cardList]);

  // Helper function to render a single card UI
  const renderCard = (c) => {
    const brand = (c.brand || "Card").toUpperCase();
    const cardNumber = `**** **** **** ${c.last4 || "••••"}`;
    const exp =
      c.exp_month && c.exp_year
        ? `${String(c.exp_month).padStart(2, "0")} / ${String(
            c.exp_year
          ).slice(-2)}`
        : "--/--";
    return (
      <AntCard
        style={{
          // height: 520,
          background:
            "linear-gradient(135deg, #f0f0f0 0%, #ffffff 70%, #f0f0f0 50%)",
          color: "#333",
          borderRadius: 16,
          boxShadow: "0 10px 20px rgba(0, 0, 0, 0.2)",
          border: "1px solid #939494ff",
          overflow: "hidden",
        }}
        bordered={false}
        bodyStyle={{ padding: "24px" }}
      >
        <Row
          justify="space-between"
          align="top"
          style={{ marginBottom: "0.5rem" }}
        >
          <Col>
            <IdcardOutlined style={{ fontSize: 36, color: "#555" }} />
          </Col>
          <Col style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 24, fontWeight: "bold" }}>{brand}</div>
            {c.is_default && <Tag color="blue">Default</Tag>}
          </Col>
        </Row>

        <Row style={{ marginBottom: "1rem" }}>
          <Col span={24}>
            <div
              style={{
                fontSize: 22,
                fontFamily: "monospace",
                letterSpacing: 3,
                textAlign: "center",
              }}
            >
              {cardNumber}
            </div>
          </Col>
        </Row>

        <Row justify="space-between" align="bottom">
          <Col>
            {/* <div
              style={{
                textTransform: "uppercase",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {c.billing_details?.name || "-"}
            </div> */}
          </Col>
          <Col
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <span>{exp}</span>
            <WifiOutlined
              style={{ fontSize: 24, transform: "rotate(90deg)" }}
            />
          </Col>
        </Row>

        <Row justify="end" gutter={8} style={{ marginTop: 24 }}>
          <Col>
            <Popconfirm
              title="Remove this card?"
              okText="Remove"
              cancelText="Cancel"
              onConfirm={() => deleteCard(c)}
            >
              <Button danger icon={<DeleteOutlined />} ghost>
                Remove
              </Button>
            </Popconfirm>
          </Col>
          <Col>
            <Button
              type="primary"
              ghost
              icon={<CheckOutlined />}
              disabled={c.is_default}
              onClick={() => makeDefaultCard(c)}
            >
              Make default
            </Button>
          </Col>
        </Row>
      </AntCard>
    );
  };

  // Logic to create slides with pairs of cards
  const createSlides = () => {
    const slides = [];
    const paymentMethods = cardList?.payment_methods || [];

    for (let i = 0; i < paymentMethods.length; i += 2) {
      const card1 = paymentMethods[i];
      const card2 = paymentMethods[i + 1]; // Will be undefined if odd number of cards

      slides.push(
        <div key={card1.id || i}>
          <Row gutter={[32, 16]} justify="center">
            <Col
              xs={24}
              md={card2 ? 12 : 24}
              style={{
                maxWidth: 410,
                margin: !card2 ? "0 auto" : undefined, // Center single card
              }}
            >
              {renderCard(card1)}
            </Col>
            {card2 && (
              <Col xs={24} md={12} style={{ maxWidth: 410 }}>
                {renderCard(card2)}
              </Col>
            )}
          </Row>
        </div>
      );
    }
    return slides;
  };

  return (
    <>
      {openModal && (
        <AddCardModal
          open={openModal}
          close={() => {
            setOpenModal(false);
            getCards();
          }}
          memberId={memberId}
        />
      )}
      <div
        className="mp-carousel"
        style={{
          width: "100%",
          position: "relative",
          overflow: "visible",
          paddingBottom: hasCards ? 24 : 0,
        }}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 12 }}
        >
          <Col>
            <div
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#0981C8",
                marginBottom: "8px",
                marginLeft: "20px",
              }}
            >
              {title}
            </div>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setOpenModal(true)}
            >
              Add Card
            </Button>
          </Col>
        </Row>

        {!hasCards ? (
          <Empty description={loading ? "Loading..." : "No cards to show"} />
        ) : (
          <Carousel arrows draggable infinite={false}>
            {createSlides()}
          </Carousel>
        )}
      </div>
    </>
  );
}